import { Modal, Form, Input, Checkbox, message } from 'antd';
import PropTypes from 'prop-types';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from 'ttag';

import Label from 'common/components/Label';
import UnstyledCollapse from 'common/components/UnstyledCollapse';

import { Description } from '@domains/Event/StyledComponents';

import { useCreateOrganizationMutation } from './queries';

const CreateModal = ({ visible, onClose }) => {
  const [form] = Form.useForm();
  const createMutation = useCreateOrganizationMutation();
  const isInternal = Form.useWatch('isInternal', form);
  const navigate = useNavigate();

  const handleSubmit = async values => {
    createMutation.mutate(
      {
        ...values,
        crmOrganizationId: isInternal ? null : values.crmOrganizationId || null,
      },
      {
        onSuccess: ({ data }) => {
          navigate(`/organization/${data.id}/events`);
          message.success(t`Organization created successfully`);
        },
        onError: () => {
          message.error(t`Failed to create organization. Please try again.`);
        },
      },
    );
  };

  const handleCancel = () => {
    form.resetFields();
    onClose(false);
  };

  return (
    <Modal
      title={t`Add Organization`}
      open={visible}
      onCancel={handleCancel}
      destroyOnHidden
      okText={t`Add Organization`}
      okButtonProps={{
        loading: createMutation.isLoading,
        htmlType: 'submit',
        form: 'create-organization-form',
      }}
      cancelText={t`Cancel`}
      cancelButtonProps={{ disabled: createMutation.isLoading }}
    >
      <Form
        form={form}
        id="create-organization-form"
        layout="vertical"
        requiredMark={false}
        onFinish={handleSubmit}
        disabled={createMutation.isLoading}
        initialValues={{
          isInternal: true,
          supportLevel: 'standard',
        }}
        preserve={false}
      >
        <Form.Item
          name="name"
          label={<Label>{t`Organization Name`}</Label>}
          rules={[
            { required: true, message: t`Organization name is required.` },
            {
              min: 3,
              max: 100,
              message: t`Organization name must be between 3 to 100 characters.`,
            },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item name="isInternal" valuePropName="checked">
          <Checkbox>
            {t`Internal Use Only`}
            <br />
            <Description>{t`This is used for internal analytics and CRM automation. This can only be changed by our engineering team.`}</Description>
          </Checkbox>
        </Form.Item>

        <UnstyledCollapse condition={!isInternal}>
          <Form.Item
            name="crmOrganizationId"
            label={<Label>{t`Hubspot Organization ID`}</Label>}
            rules={[
              { max: 64, message: t`Hubspot Organization ID has max length of 64 characters.` },
            ]}
          >
            <Input />
          </Form.Item>
        </UnstyledCollapse>
      </Form>
    </Modal>
  );
};

CreateModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CreateModal;
